package com.essntl.features.proposal.ui.allproposal

import com.essntl.core.utils.utils.loadingmanager.LoadingManager
import com.essntl.core.utils.viewmodel.*
import com.essntl.features.area.domain.repository.AreaRepository
import com.essntl.features.profile.domain.repository.ProfileRepository
import com.essntl.features.proposal.domain.model.ProposalFilterOptions
import com.essntl.features.proposal.domain.model.ProposalModel
import com.essntl.features.proposal.domain.repository.ProposalRepository
import com.essntl.toastmanager.ToastManager
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime

class AllProposalViewModel(
    private val proposalRepository: ProposalRepository,
    private val areaRepository: AreaRepository,
    private val profileRepository: ProfileRepository,
    private val toastManager: ToastManager,
    override val scope: CoroutineScope,
    override val loadingManager: LoadingManager,
    private val state: MutableStateFlow<AllProposalState>,
) : ViewModel {

    init {
        getAreas()
        getAllProfile()
    }

    fun onEvent(event: AllProposalEvent) {
        when (event) {
            is AllProposalEvent.GetAllProposal -> getAllProposal()
            is AllProposalEvent.FilterProposal -> filterProposal(event.filterOptions)
            is AllProposalEvent.LoadMoreProposals -> loadMoreProposals()
            is AllProposalEvent.GetProposalHistoryList -> getProposalHistoryList(event)
            is AllProposalEvent.SelectProposal -> selectProposal(event)
            is AllProposalEvent.MakeProposalCopy -> makeProposalCopy(event)
            is AllProposalEvent.UpdateProposalStatus -> updateProposalStatus(event)
            is AllProposalEvent.UpdateProposalOwner -> updateProposalOwner(event)
            is AllProposalEvent.UpdateProposal -> editProposal(event)
            is AllProposalEvent.ClearProposalState -> clearProposalState()
        }
    }

    private fun getAllProposal() {
        state.update {
            it.copy(getAllProposal = PaginationState.loading())
        }

        loadMoreProposals(ignoreLoading = true)
    }

    private var loadMoreProposalsJob: Job? = null
    private fun loadMoreProposals(ignoreLoading: Boolean = false) {
        if (!ignoreLoading && state.value.getAllProposal.isLoading) return

        loadMoreProposalsJob?.cancel()
        loadMoreProposalsJob = getPaginationData(
            getData = {
                val nextPage = state.value.getAllProposal.loadedPages + 1L
                proposalRepository.getAll(
                    page = nextPage,
                    filterOptions = state.value.filterOptions,
                )
            },
            getState = { state.value.getAllProposal },
            setState = { newState -> state.update { it.copy(getAllProposal = newState) } },
            ignoreLoading = ignoreLoading,
        )
    }

    private fun getAreas() =
        getData(
            getData = { areaRepository.getAll() },
            getState = { state.value.getAllArea },
            setState = { newState -> state.update { it.copy(getAllArea = newState) } },
        )

    private fun getAllProfile() =
        getData(
            getData = { profileRepository.getAll() },
            getState = { state.value.getAllProfile },
            setState = { newState -> state.update { it.copy(getAllProfile = newState) } },
        )

    private fun getProposalHistoryList(event: AllProposalEvent.GetProposalHistoryList) =
        getData(
            getData = { proposalRepository.getProposalHistoryList(event.proposalId) },
            getState = { state.value.getAllProposalHistoryMap[event.proposalId] ?: State() },
            setState = { data ->
                state.update {
                    it.copy(
                        getAllProposalHistoryMap = state.value.getAllProposalHistoryMap.toMutableMap().apply {
                            this[event.proposalId] = data
                        },
                    )
                }
            },
        )

    private fun selectProposal(event: AllProposalEvent.SelectProposal) {
        state.value = state.value.copy(
            selectedProposalId = event.proposalId,
        )
    }

    private fun makeProposalCopy(event: AllProposalEvent.MakeProposalCopy) {
        val proposalId = event.proposal.proposalId ?: return
        if (state.value.updateProposalLoading.contains(event.proposal.proposalId)) return

        state.value = state.value.copy(
            updateProposalLoading = state.value.updateProposalLoading + proposalId,
        )

        scope.launch {
            // Delete proposal services
            val deleteAllProposalServiceResult = proposalRepository.deleteAllProposalService(proposalId)

            if (deleteAllProposalServiceResult.isFailure) return@launch

            // Get proposal history services
            val getAllProposalResultService = proposalRepository.getAllProposalService(event.proposal.id)
            val proposalServiceList = getAllProposalResultService.getOrNull() ?: return@launch

            // Add a copy of proposal history services to proposal
            val addAllProposalResultService = proposalRepository.addAllProposalService(
                proposalServiceList.map {
                    it.copy(proposalId = proposalId)
                },
            )
            addAllProposalResultService.getOrNull() ?: return@launch

            val newProposal = event.proposal.copy(
                id = proposalId,
                travelAgent = event.proposal.travelAgent,
                owner = event.proposal.owner,
                proposalId = null,
            )

            // Update proposal
            proposalRepository.updateProposal(newProposal)
                .onSuccess { proposal ->
                    val proposalList = state.value.getAllProposal.data.orEmpty().toMutableList()
                    val proposalIndex = proposalList.indexOfFirst { it.id == proposal.id }
                    if (proposalIndex != -1)
                        proposalList[proposalIndex] = proposal

                    state.value = state.value.copy(
                        getAllProposal = state.value.getAllProposal.copy(
                            data = proposalList,
                        ),
                        updateProposalLoading = state.value.updateProposalLoading - proposalId,
                    )

                    toastManager.success(description = "Proposal copied successfully")
                }
                .onFailure {
                    state.value = state.value.copy(
                        updateProposalLoading = state.value.updateProposalLoading - proposalId,
                    )

                    toastManager.error(description = "Failed to copy proposal")
                }
        }
    }

    private fun updateProposalStatus(event: AllProposalEvent.UpdateProposalStatus) {
        if (state.value.updateProposalLoading.contains(event.proposal.id)) return

        state.value = state.value.copy(
            updateProposalLoading = state.value.updateProposalLoading + event.proposal.id,
        )

        val proposal = ProposalModel.empty.copy(
            id = event.proposal.id,
            status = event.status,
            owner = event.proposal.owner,
            travelAgent = event.proposal.travelAgent,
            statusUpdatedAt = Clock.System.now().toLocalDateTime(TimeZone.UTC).date.toString(),
        )

        scope.launch {
            proposalRepository.updateProposal(proposal)
                .onSuccess { proposal ->
                    println("updateProposal $proposal")
                    val proposalList = state.value.getAllProposal.data.orEmpty().toMutableList()
                    val proposalIndex = proposalList.indexOfFirst { it.id == proposal.id }
                    if (proposalIndex != -1)
                        proposalList[proposalIndex] = proposal

                    state.value = state.value.copy(
                        getAllProposal = state.value.getAllProposal.copy(
                            data = proposalList,
                        ),
                        updateProposalLoading = state.value.updateProposalLoading - event.proposal.id,
                    )

                    toastManager.success(
                        title = "Proposal updated",
                        description = "Proposal status updated successfully",
                    )

                    if (proposal.status == ProposalModel.Status.Confirmed
                        || proposal.status == ProposalModel.Status.ItineraryReady
                    )
                        proposalRepository.updateProposalToItinerary(proposal = proposal)
                }
                .onFailure {
                    state.value = state.value.copy(
                        updateProposalLoading = state.value.updateProposalLoading - event.proposal.id,
                    )
                }
        }
    }

    private fun updateProposalOwner(event: AllProposalEvent.UpdateProposalOwner) {
        // Prevent multiple requests for the same proposal
        if (event.proposal.id in state.value.updateProposalOwnerLoading) return

        // Check if the new owner is the same as the current owner
        val proposal = state.value.getAllProposal.data.find { it.id == event.proposal.id }
        if (proposal?.owner?.id == event.owner.id) return

        state.value = state.value.copy(
            updateProposalOwnerLoading = state.value.updateProposalOwnerLoading + event.proposal.id,
        )

        val newProposal = ProposalModel.empty.copy(
            id = event.proposal.id,
            owner = event.owner,
            travelAgent = event.proposal.travelAgent,
        )

        scope.launch {
            proposalRepository.updateProposal(newProposal)
                .onSuccess { proposal ->
                    val proposalList = state.value.getAllProposal.data.orEmpty().toMutableList()
                    val proposalIndex = proposalList.indexOfFirst { it.id == proposal.id }
                    if (proposalIndex != -1)
                        proposalList[proposalIndex] = proposal

                    state.value = state.value.copy(
                        getAllProposal = state.value.getAllProposal.copy(
                            data = proposalList,
                        ),
                        updateProposalOwnerLoading = state.value.updateProposalOwnerLoading - event.proposal.id,
                    )
                }
                .onFailure {
                    state.value = state.value.copy(
                        updateProposalOwnerLoading = state.value.updateProposalOwnerLoading - event.proposal.id,
                    )
                }
        }
    }

    private fun editProposal(event: AllProposalEvent.UpdateProposal) {
        getData(
            getData = { proposalRepository.updateProposal(event.proposal) },
            getState = { state.value.addEditProposal },
            setState = { state.value = state.value.copy(addEditProposal = it) },
            onSuccess = { proposal ->
                val proposalList = state.value.getAllProposal.data.toMutableList()
                val proposalIndex = proposalList.indexOfFirst { it.id == proposal.id }
                if (proposalIndex != -1)
                    proposalList[proposalIndex] = proposal

                state.value = state.value.copy(
                    getAllProposal = state.value.getAllProposal.copy(
                        data = proposalList,
                    ),
                )

                toastManager.success(description = "Proposal updated successfully")
            },
        )
    }

    private fun filterProposal(filterOptions: ProposalFilterOptions) {
        if (filterOptions == state.value.filterOptions) return

        state.update {
            it.copy(
                filterOptions = filterOptions,
            )
        }

        getAllProposal()
    }

    private fun clearProposalState() {
        state.value = state.value.copy(
            addEditProposal = State(),
        )
    }
}